import { motion } from 'framer-motion'
import { Check, Gift, Layers3, CreditCard, Clock3 } from 'lucide-react'
import type { PublicPlanView } from '@/utils/propostaPublicScope'

type Props = {
  plan: PublicPlanView
  index: number
}

export function PlanScopeCard({ plan, index }: Props) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.35 }}
      className="relative overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] shadow-sm"
    >
      <div className="p-5 sm:p-6 space-y-5">
        <header>
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
                style={{
                  color: plan.tipoColor,
                  backgroundColor: `${plan.tipoColor}18`,
                }}
              >
                {plan.tipoLabel}
              </span>
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-[var(--text-primary)] leading-snug">
              {plan.title}
            </h2>
            {plan.subtitle && (
              <p className="text-sm text-[var(--text-muted)] leading-relaxed">{plan.subtitle}</p>
            )}
          </div>
        </header>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-start gap-2.5 rounded-xl bg-[var(--bg-dark)] border border-[var(--border)] p-3">
            <CreditCard size={15} className="text-[var(--brand-primary)] mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
                Pagamento
              </p>
              <p className="text-xs text-[var(--text-primary)] mt-0.5">{plan.paymentDetails}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5 rounded-xl bg-[var(--bg-dark)] border border-[var(--border)] p-3">
            <Clock3 size={15} className="text-[var(--brand-primary)] mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
                Vigência
              </p>
              <p className="text-xs text-[var(--text-primary)] mt-0.5">{plan.loyaltyLabel}</p>
            </div>
          </div>
        </div>

        {plan.bonus && (
          <div className="flex gap-3 rounded-xl border border-[var(--brand-primary)]/25 bg-[var(--brand-primary)]/10 p-4">
            <Gift size={18} className="text-[var(--brand-primary)] shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--brand-primary)]">Bônus</p>
              <p className="text-sm text-[var(--text-primary)] mt-1 whitespace-pre-line">{plan.bonus}</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Layers3 size={15} className="text-[var(--brand-primary)]" />
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--text-muted)]">
              Escopo do plano
            </h3>
          </div>

          {plan.categories.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] rounded-xl border border-dashed border-[var(--border)] p-4">
              Escopo detalhado disponível sob consulta com a equipe Rocket Search.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {plan.categories.map((category) => (
                <div
                  key={`${plan.item.id}-${category.category}`}
                  className="flex flex-col rounded-xl border border-[var(--border)] overflow-hidden bg-[var(--bg-card)] h-full shadow-sm"
                >
                  <div className="px-4 py-3 bg-[var(--bg-dark)] border-b border-[var(--border)]">
                    <p className="text-xs font-semibold text-[var(--text-primary)] leading-snug">
                      {category.category}
                    </p>
                    <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                      {category.services.length}{' '}
                      {category.services.length === 1 ? 'item incluído' : 'itens incluídos'}
                    </p>
                  </div>
                  <ul className="flex-1 divide-y divide-[var(--border)]">
                    {category.services.map((service) => (
                      <li
                        key={`${category.category}-${service.name}`}
                        className="flex items-start gap-2.5 px-3 py-2 hover:bg-[var(--bg-dark)] transition-colors"
                      >
                        <span
                          className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                          style={{
                            backgroundColor: `${plan.tipoColor}20`,
                            color: plan.tipoColor,
                          }}
                        >
                          <Check size={10} strokeWidth={3} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] text-[var(--text-primary)] leading-snug">
                            {service.name}
                            {service.isPartial && (
                              <span className="ml-1 text-[10px] font-medium text-amber-400/90">
                                (parcial)
                              </span>
                            )}
                          </p>
                          {service.quantity && service.quantity !== '1' && (
                            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                              Qtd: {service.quantity}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.article>
  )
}
